import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  AutoCompleteOptionItem,
  AutoCompleteTransformFn,
  AutocompleteWithControlsConfig,
} from '@shared/modules/autocomplete-with-controls';
import { AbstractFormContainer } from '@shared/classes/abstract-form-container';
import { NotHighlightPristineErrorStateMatcher } from '@shared/classes/not-highlight-pristine-error-state-matcher';
import { TypedFormControl } from '@shared/classes/typed-form-control';
import { BuildingErrors, CityErrors, StreetErrors } from '@shared/errors/error-messages';
import { StreetMaskValidator, TemplateStreetMaskValidator } from '@shared/utils/address.validator';
import { getError } from '@shared/utils/error.utils';
import { TemplateCitySearchResultMaskValidator, cityResultMaskValidator } from '@shared/validators/city-search-result-mask.validator';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { AddressService } from '../../../../../services/address.service';
import { CitySearchResult, StreetSearchResult } from '@models/address.model';

@Component({
  selector: 'np-inv-address-doors',
  templateUrl: 'address-doors.component.html',
  styleUrls: ['./address-doors.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressDoorsComponent extends AbstractFormContainer implements OnInit, TypedFormControl {
  protected readonly getError = getError;
  protected readonly cityErrors = CityErrors;
  protected readonly streetErrors = StreetErrors;
  protected readonly buildingErrors = BuildingErrors;

  readonly type = 'Doors';

  editForm: UntypedFormGroup;
  @Input() showFlat = true;

  @HostBinding('attr.data-test-id') a = 'AddressTypeDoors';

  @Input()
  controlName = 'DoorsAddress';

  @Input()
  controlTypeName = 'type';

  @Input()
  templateValidation: boolean;

  @Input()
  configCity: AutocompleteWithControlsConfig = {
    ariaLabel: 'Населений пункт',
    placeholder: 'Населений пункт',
    arrow: false,
    errorStateMatcher: new NotHighlightPristineErrorStateMatcher(),
  };

  @Input()
  configStreet: AutocompleteWithControlsConfig = {
    ariaLabel: 'Вулиця',
    placeholder: 'Вулиця',
    arrow: false,
    errorStateMatcher: new NotHighlightPristineErrorStateMatcher(),
  };

  @Input()
  configBuilding: AutocompleteWithControlsConfig = {
    ariaLabel: 'Будинок',
    placeholder: 'Будинок',
    arrow: false,
    errorStateMatcher: new NotHighlightPristineErrorStateMatcher(),
  };

  @Input()
  configRoom: AutocompleteWithControlsConfig = {
    ariaLabel: 'Квартира',
    placeholder: 'Квартира',
    arrow: false,
    errorStateMatcher: new NotHighlightPristineErrorStateMatcher(),
  };

  cityOptions: Array<AutoCompleteOptionItem<CitySearchResult>> = [];

  streetOptions: Array<AutoCompleteOptionItem<StreetSearchResult>> = [];

  @Input()
  latestCity: string | CitySearchResult;

  @Output()
  onCitySelected = new EventEmitter<CitySearchResult | string>();

  constructor(private fb: UntypedFormBuilder,
              private cd: ChangeDetectorRef,
              private addressService: AddressService,
              ) {
    super();
  }

  get cityControl(): AbstractControl | null {
    return this.editForm.get('city');
  }

  get streetControl(): AbstractControl | null {
    return this.editForm.get('street');
  }

  get buildingControl(): AbstractControl | null {
    return this.editForm.get('building');
  }

  get roomControl(): AbstractControl | null {
    return this.editForm.get('room');
  }

  get city(): CitySearchResult | null {
    return this.editForm.get('city').value;
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.templateValidation) {
      Object.keys(this.form.get(this.controlName)['controls']).forEach(key => {
        this.form.controls[this.controlName]['controls'][key].clearValidators();
      });
      this.form.get(`${this.controlName}.city`).setValidators([TemplateCitySearchResultMaskValidator()]);
      this.form.get(`${this.controlName}.street`).setValidators([
        Validators.required, TemplateStreetMaskValidator()
      ]);
      this.form.get(`${this.controlName}.building`).setValidators([Validators.required]);
    }
    this.streetControl.markAsPristine();
    this.streetControl.updateValueAndValidity();
  }

  addFormControls(): void {
    if (this.form) {
      this.createControl();
    }
  }

  createControl(): void {
    this.editForm = this.fb.group({
      city: [this.latestCity ? this.latestCity : '', [Validators.required, cityResultMaskValidator()]],
      street: [{value: '', disabled: !this.latestCity}, [Validators.required, StreetMaskValidator()]],
      building: [{value: '', disabled: true}, [Validators.required]],
      room: [{value: '', disabled: true}],
    });
    this.form.setControl(this.controlName, this.editForm);
    this.form.get(this.controlTypeName).setValue(this.type);
    this.initFormSubscriptions();
  }

  cityTransformFn: AutoCompleteTransformFn<CitySearchResult> =
    (value: CitySearchResult) => {
      if (value === null) {
        return '';
      }
      return typeof value === 'string' ? value : value.Present || '';
    };

  streetTransformFn: AutoCompleteTransformFn<StreetSearchResult> =
    (value: StreetSearchResult) => {
      if (value === null) {
        return '';
      }
      return typeof value === 'string' ? value : value.Present || '';
    };

  initFormSubscriptions(): void {
    this.initCitySubscriptions();
    this.initStreetSubscriptions();
  }

  initCitySubscriptions(): void {
    this.cityControl.statusChanges
      .subscribe(status => {
        const cityValue = this.cityControl.value;
        if (status === 'VALID' && cityValue?.Ref) {
          this.onCitySelected.next(this.city);
          this.streetControl.enable();
          this.streetControl.markAsPristine();
          this.streetControl.updateValueAndValidity();
          if (!this.templateValidation) {
            this.configStreet = {...this.configStreet, focused: true};
          }
          this.cd.detectChanges();
        } else {
          this.streetOptions = [];
          this.streetControl.reset('');
          this.streetControl.disable();
        }
      });
    this.cityControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
      )
      .subscribe(value => {
        if (typeof value === 'string' && value.length >= 2) {
          this.loadCities(value);
        } else {
          this.cityOptions = [];
        }
        this.cd.detectChanges();
      });
  }

  initStreetSubscriptions(): void {
    this.streetControl.statusChanges
      .subscribe(status => {
        const streetValue = this.streetControl.value;
        if (status === 'VALID' && streetValue?.SettlementStreetRef) {
          this.buildingControl.enable();
          this.buildingControl.markAsPristine();
          this.buildingControl.updateValueAndValidity();
          this.roomControl.enable();
          if (!this.templateValidation) {
            this.configBuilding = {...this.configBuilding, focused: true};
          }
          this.cd.detectChanges();
        } else {
          this.buildingControl.reset('');
          this.buildingControl.disable();
          this.roomControl.reset('');
          this.roomControl.disable();
        }
      });
    this.streetControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
      )
      .subscribe((value: string) => {
        if (typeof value === 'string' && value.length >= 2) {
          this.loadStreets(value);
        } else {
          this.streetOptions = [];
        }
        this.cd.detectChanges();
      });
  }

  loadCities(nameQuery: string): void {
    this.addressService.getCities({ name: nameQuery })
      .pipe(
        tap(response => this.cityOptions = response.Addresses.map(city => ({ option: city }))),
      )
      .subscribe(() => this.cd.detectChanges());
  }

  loadStreets(nameQuery: string): void {
    this.addressService.getStreets({ name: nameQuery, localityRef: this.city.Ref })
      .pipe(
        tap(response => this.streetOptions = response.Addresses.map(street => ({ option: street }))),
      )
      .subscribe(() => this.cd.detectChanges());
  }
}
